<script lang="ts" setup>
import { useNotificationStore } from '@/store/notifications'
import UtilIconButton from '@/components/util/UtilIconButton.vue'
import IconWarning from '@/components/icons/IconWarning.vue'
import IconInfo from '@/components/icons/IconInfo.vue'
import UtilIcon from '@/components/util/UtilIcon.vue'

const notificationsStore = useNotificationStore()

function handleDismiss(id: number) {
  const index = notificationsStore.notifications.findIndex((n) => n.id === id)
  if (index !== -1) {
    notificationsStore.dismissNotification(index)
  }
}
</script>

<template>
  <div class="notifications">
    <TransitionGroup
      name="notification"
      tag="ol"
      class="notifications__wrapper"
      role="status"
      aria-live="polite"
      aria-relevant="additions"
    >
      <li
        v-for="notification in notificationsStore.notifications"
        :key="notification.id"
        class="notification"
        :class="`notification--${notification.type}`"
      >
        <IconWarning
          v-if="notification.type === 'error'"
          class="notification__end"
        />
        <template v-if="notification.icon">
          <IconInfo
            v-if="notification.icon === 'IconInfo'"
            class="notification__end"
          />
          <UtilIcon
            v-else
            :icon="notification.icon"
            class="notification__end"
          />
        </template>
        <div class="notification__text">
          {{ notification.message }}
        </div>
        <UtilIconButton
          v-if="notification.dismissable"
          icon="circle-cross"
          @click="handleDismiss(notification.id)"
        />
      </li>
    </TransitionGroup>
  </div>
</template>

<style lang="scss" scoped>
.notifications {
  width: var(--width-100);
  position: fixed;
  bottom: var(--runtime-app-bottom-inset, 0px);
  left: 0;
  z-index: 1;
}
.notifications__wrapper {
  width: var(--width-100);
  max-width: var(--large-screen-width);
  margin: 0 auto;
  padding-left: 0;
  list-style-type: none;
}
.notification {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  background-color: white;
  border: 1px solid transparent;
  border-radius: var(--border-radius-4);
  padding: 20px;
  margin: 0 1rem 10px;
}
.notification__text {
  flex-grow: 1;
}
.notification__end {
  flex-shrink: 0;
  flex-grow: 0;
}

.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease-in;
}

.notification-enter-from,
.notification-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.notification--error {
  background-color: var(--red100);
  border-color: var(--red500);
  color: var(--red500);
}
.notification--info {
  background-color: var(--purple150);
  border-color: var(--purple150);
  color: var(--grey800);
  font-size: var(--font-size-14);
}

.notification--success {
  background-color: var(--green50);
  border-color: var(--green200);
  color: var(--grey800);
}
</style>
